import { Progress as MantineProgress, Tooltip as MantineTooltip } from "@mantine/core";
import * as React from "react";

import type { ElementProps, SizeValue, StyleProps, ThemeColor } from "../primitives/style-props";

export interface ProgressSection {
  /** Portion of the bar, expressed as a percent. */
  value: number;
  color: ThemeColor;
  label?: string;
  tooltip?: React.ReactNode;
}

export interface ProgressProps extends ElementProps<"div">, StyleProps {
  /** Percent of the bar to fill when `sections` is absent. */
  value?: number;
  color?: ThemeColor;
  size?: SizeValue;
  radius?: SizeValue;
  striped?: boolean;
  animate?: boolean;
  label?: string;
  /** Multiple independently coloured portions of the bar. */
  sections?: ProgressSection[];
}

// Built outside the `.map` callback: the key belongs on the wrapper the callback returns, not on this inner element.
function renderProgressSection(section: ProgressSection, striped: boolean | undefined, animate: boolean | undefined) {
  return (
    <MantineProgress.Section value={section.value} color={section.color} striped={striped} animated={animate} withAria={false}>
      {section.label && <MantineProgress.Label>{section.label}</MantineProgress.Label>}
    </MantineProgress.Section>
  );
}

/** Mantine 6's section data contract, including optional portalled tooltips, on Mantine 7. */
export const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(function Progress(
  { value, color, radius = "sm", striped, animate, label, sections, "aria-label": ariaLabel, ...props },
  ref,
) {
  return (
    <MantineProgress.Root {...props} radius={radius} ref={ref}>
      {sections ? (
        sections.map((section, index) => {
          const bar = renderProgressSection(section, striped, animate);

          return section.tooltip ? (
            <MantineTooltip.Floating key={index} label={section.tooltip}>
              {bar}
            </MantineTooltip.Floating>
          ) : (
            <React.Fragment key={index}>{bar}</React.Fragment>
          );
        })
      ) : (
        <MantineProgress.Section
          value={value ?? 0}
          color={color}
          striped={striped}
          animated={animate}
          withAria={false}
          role="progressbar"
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={value}
          aria-label={ariaLabel}
        >
          {label && <MantineProgress.Label>{label}</MantineProgress.Label>}
        </MantineProgress.Section>
      )}
    </MantineProgress.Root>
  );
});
